/**
 * Metadata for the public storefront routes, as data rather than JSX, so the
 * build can write it into the served HTML for crawlers and assistants that
 * never run the application, and the router can apply the same values on
 * navigation.
 */
export const SITE_ORIGIN = "https://studio.odingard.com";
export const OG_IMAGE_PATH = "/media/odingard-studio-og.png";

export interface RouteMeta {
  title: string;
  description: string;
}

export const ROUTE_META: Record<string, RouteMeta> = {
  "/": {
    title: "Win more government contracts without adding audit risk | Odingard Studio",
    description:
      "Governed AI for GovCon and regulated teams: contracts, proposals and policy in one account, with grounded cited output, tenant isolation and audit ready evidence. Real prices, free trial, no sales call.",
  },
  "/marketplace": {
    title: "Marketplace: buy governed AI applications by the firm | Odingard Studio",
    description:
      "Every Odingard application, bundle and service in one catalog. Provision Contract AI, Proposal AI or Policy AI instantly with a free trial, priced per firm rather than per seat.",
  },
  "/pricing": {
    title: "Pricing for governed AI, per firm not per seat | Odingard Studio",
    description:
      "See real prices and start free today: no sales call, no per seat surprise. Contract AI, Proposal AI and Policy AI, the Odingard One bundle and the GovCon package, priced per firm per month.",
  },
  "/solutions": {
    title: "Solutions for GovCon, legal and compliance teams | Odingard Studio",
    description:
      "How government contractors and regulated teams use Odingard Studio: build a compliant bid before the deadline, keep obligations and flow downs auditable, and prove a policy is operating.",
  },
  "/security": {
    title: "Security, data handling and certification status | Odingard Studio",
    description:
      "Tenant isolation, entitlements that fail closed and hash chained audit. Your documents are never used to train public or shared AI models, and certification status is stated per framework.",
  },
  "/about": {
    title: "About Odingard Studio, and who operates it | Odingard Studio",
    description:
      "The operating entity behind Odingard Studio, who runs it, and why governed AI for regulated and government contracting work is built this way.",
  },
  "/customers": {
    title: "Customers | Odingard Studio",
    description:
      "Who uses Odingard Studio for governed AI on contracts, proposals and policy, and what it changed for them.",
  },
  "/resources": {
    title: "Resources for running governed AI | Odingard Studio",
    description:
      "Guides, documentation and reference material for running governed AI applications on Odingard Studio.",
  },
  "/contact": {
    title: "Contact Odingard | Odingard Studio",
    description: "Talk to Odingard about applications, professional services, procurement or security review.",
  },
};

/**
 * The three applications a customer can buy today, with the price /pricing
 * quotes. A roadmap application has no offer here: an assistant reading this
 * page must not tell a buyer they can purchase something that does not exist.
 */
export const PRODUCT_OFFERS: { name: string; description: string; monthlyUsd: number; path: string }[] = [
  {
    name: "Contract AI",
    description:
      "Keep every obligation, renewal and flow down auditable in one system of record, with answers cited back to the clause.",
    monthlyUsd: 149,
    path: "/marketplace/apps/app-contract",
  },
  {
    name: "Proposal AI",
    description:
      "Turn a solicitation into a compliant, cited bid before the deadline, with a requirement by requirement compliance matrix.",
    monthlyUsd: 149,
    path: "/marketplace/apps/app-proposal",
  },
  {
    name: "Policy AI",
    description:
      "Write, approve and prove the policy an auditor is about to ask for, with immutable versions and framework mapping.",
    monthlyUsd: 129,
    path: "/marketplace/apps/app-policy",
  },
];

function organizationJsonLd(): unknown {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Odingard",
    url: SITE_ORIGIN,
    logo: `${SITE_ORIGIN}/odingard-logo.png`,
    description:
      "Odingard builds governed AI applications for government contractors and regulated teams: contracts, proposals and policy, with grounded cited output and audit ready evidence.",
    sameAs: ["https://www.odingard.com"],
  };
}

function productsJsonLd(): unknown[] {
  return PRODUCT_OFFERS.map((p) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${p.name} by Odingard Studio`,
    description: p.description,
    brand: { "@type": "Brand", name: "Odingard Studio" },
    url: `${SITE_ORIGIN}${p.path}`,
    offers: {
      "@type": "Offer",
      price: p.monthlyUsd,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: `${SITE_ORIGIN}/pricing`,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: p.monthlyUsd,
        priceCurrency: "USD",
        unitText: "firm per month",
        billingIncrement: 1,
      },
    },
  }));
}

/** Every JSON-LD document the served HTML carries. */
export function structuredData(): unknown[] {
  return [organizationJsonLd(), ...productsJsonLd()];
}
